import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { isFetchBaseQueryError } from 'lib-shared-auth';
import type { ApiResponse } from 'lib-shared-types-api';

import { AiFeedback } from './aiFeedback.types';
import { AiStyleGuide } from './aiStyleGuides.types';

type Match = {
  context_combination: string[];
  content: string;
  id: string;
};

type AiApiResponse<TEntity extends Record<string, unknown>> = {
  /** The feedback id for providing feedback through the feedback endpoint. */
  readonly feedback_id: string;
  /** The total number of tokens used for processing. */
  readonly total_tokens: number;
} & TEntity;

type AiTextApiResponse = AiApiResponse<{
  /** The raw text response. */
  readonly text: string;
}>;

type AiStreamQueryHookMeta = {
  /** Whether text is still streaming or not */
  readonly isStreaming: boolean;
  /**
   * When true, indicates that the query is currently loading for the first
   * time, and has no data yet
   */
  readonly isLoading: boolean;
  /**
   * When true, indicates that the stream encountered an error, either at the
   * initial request or mid stream.
   */
  readonly isError: boolean;
  /**
   * When true, indicates that the stream has ended and successfully received
   * the entire text response.
   */
  readonly isSuccess: boolean;
};

type AiWritingImprovementArg = {
  /** The model name. */
  readonly promptName: AIImproveWritingPromptName;
  /** The text to improve. */
  readonly text: string;
  /** The last message from the conversation */
  readonly lastMessage: string;
  /** Custom instructions/prompt for the AI */
  readonly instructions?: string;
  /** Writing style guide context */
  readonly contexts?: {
    readonly channels?: AiStyleGuide['contexts']['channels'][number];
    readonly features?: AiStyleGuide['contexts']['features'][number];
  };
  /** The csid */
  readonly csid?: string;
};

export type ReadAiTextTranslationApiResponse = AiTextApiResponse & {
  meta: AiStreamQueryHookMeta;
};
export type CreateAiTextTranslationApiResponse = AiTextApiResponse;
export type CreateAiTextTranslationApiArg = {
  /** The language to translate the given text to. */
  readonly language: string;
  /** The text to translate. */
  readonly text: string;
  /** The csid */
  readonly csid?: string;
  /** The content type of the text (text or html). */
  readonly contentType?: 'text' | 'html';
};
export type ReadAiTextTranslationApiArg = {
  /** The language to translate the given text to. */
  readonly language: string;
  /** The text to translate. */
  readonly text: string;
  /** The csid */
  readonly csid?: string;
  /** The content type of the text (text or html). */
  readonly contentType?: 'text' | 'html';
};

export type CreateAiWritingImprovementResponse = AiTextApiResponse;
export type CreateAiWritingImprovementArg = AiWritingImprovementArg;

export type ReadAiCheckGrammarAndSpellingApiResponse = AiTextApiResponse & {
  meta: AiStreamQueryHookMeta;
};
export type ReadAiCheckGrammarAndSpellingApiArg = {
  /** The text to check for grammar and spelling. */
  readonly text: string;
  /** The csid */
  readonly csid?: string;
};

export type CreateAiFeedbackApiArg = {
  /**
   * The feedback id from an endpoint that produced or contains AI data, e.g.
   * conversation summary, translation.
   */
  readonly feedbackId: string;
  /** The feedback. */
  readonly feedback: readonly AiFeedback[];
};

export type AIImproveWritingPromptName =
  | 'repeat-input'
  | 'custom_writing_improvement'
  | 'make_friendly'
  | 'make_formal'
  | 'make_concise'
  | 'expand_reply'
  | 'match_tone'
  | 'make_active_voice';

export type ReadAiWritingImprovementArg = AiWritingImprovementArg;

export type ReadAiWritingImprovementResponse = AiTextApiResponse & {
  meta: AiStreamQueryHookMeta;
};

/** Created style guide */
export type CreateAIWritingStyleGuidesResponse = ApiResponse<AiStyleGuide>;

export type CreateAiWritingStyleGuideArg = Omit<
  AiStyleGuide,
  'created_at' | 'id'
>;
export type ReadAIWritingStyleGuidesResponse = ApiResponse<{
  /** The list of writing style guides. */
  readonly style_guides: readonly AiStyleGuide[];
}>;

export type ReadAIWritingStyleGuideResponse = ApiResponse<AiStyleGuide>;
export type ReadAIWritingStyleGuideArg = {
  /** The id of the writing style guide to read. */
  readonly styleguideId: string;
};

export type DeleteAiWritingStyleGuideArg = {
  /** The id of the writing style guide to delete. */
  readonly styleguideId: string;
};

export type ReadAiWritingGuideMatchApiArg = {
  features: AiStyleGuide['contexts']['features'][number];
  channels?: AiStyleGuide['contexts']['channels'][number];
  csid?: string;
};

export type ReadAiStyleGuideMatchApiResponse = ApiResponse<
  { match: Match },
  Record<string, unknown>
>;

type DetailedError = FetchBaseQueryError & {
  data: {
    detail: string;
  };
};

/**
 * Type predicate to narrow an unknown error to an object with the property
 * 'data' containing a string 'detail' property
 */
export const isDetailedError = (error: unknown): error is DetailedError => {
  if (isFetchBaseQueryError(error)) {
    const errorData = (error as DetailedError).data;
    return (
      typeof errorData === 'object' &&
      errorData != null &&
      'detail' in errorData &&
      typeof errorData.detail === 'string'
    );
  }
  return false;
};
